/**
 * The in-app Help section.
 *
 * Deliberately built into the builder rather than kept as an external doc: an
 * author decides how to draw something *while looking at the studio*, so the
 * explanation belongs one tab away, not in a wiki they have to go and find.
 *
 * It reads the same regardless of session — it describes the tool, needs no
 * data, and is also reachable at `/help` for linking and for someone who has
 * not signed in yet.
 *
 * The content is kept true to what the app actually does. When a rule here and
 * the code disagree, the code is right and this is the bug.
 */

const FORMS: Array<{ name: string; draws: string; when: string }> = [
  { name: 'Bar chart', draws: 'Horizontal bars, values at the tip', when: 'Comparing a measure across named things' },
  { name: 'Column chart', draws: 'Vertical columns on an ordered axis', when: 'Comparing periods or ordered buckets' },
  { name: 'Grouped bar chart', draws: 'Columns clustered side by side', when: 'Comparing sub-categories within groups' },
  { name: 'Leaderboard', draws: 'Ranked rows with a magnitude track', when: 'Top-N where the ranking is the point' },
  { name: 'Ranked columns', draws: 'Several ranked lists side by side, per-row hover detail', when: 'Top-N compared across segments (Superset Top Charts)' },
  { name: 'Stat tile', draws: 'One figure, its change, a sparkline', when: 'A single number that matters' },
  { name: 'Gauge', draws: 'One value on an arc against a range', when: 'A single value against a target or ceiling' },
  { name: 'Radar chart', draws: 'One polygon per series over shared axes', when: 'Comparing profiles across a few measures' },
  { name: 'Waterfall', draws: 'Floating bars of gains and losses', when: 'How a figure is built up or drawn down' },
  { name: 'Trend', draws: 'Lines with a crosshair readout', when: 'A measure over time' },
  { name: 'Line chart', draws: 'Bare lines, no fill', when: 'Several measures over time, compared' },
  { name: 'Spline chart', draws: 'A smoothed line through the points', when: 'A gentle trend over time' },
  { name: 'Step line', draws: 'The value holds flat, then jumps', when: 'A measure that changes in discrete steps' },
  { name: 'Area chart', draws: 'Lines with the area beneath filled', when: 'A cumulative measure over time' },
  { name: 'Stacked area', draws: 'Series stacked to a running total, filled', when: 'How a total splits over time' },
  { name: 'Stacked columns', draws: 'Parts of each period stacked to its total', when: 'How a total splits, period by period' },
  { name: 'Pie chart', draws: 'A full circle split into slices', when: 'Composition, at a glance only' },
  { name: 'Donut', draws: 'A pie with an open centre for the total', when: 'Composition with the total called out' },
  { name: 'Treemap', draws: 'Nested rectangles sized by value', when: 'Composition across many segments' },
  { name: 'Sunburst', draws: 'Concentric rings, group inside, members outside', when: 'Hierarchical composition' },
  { name: 'Funnel', draws: 'Stages narrowing to an outcome', when: 'A process with drop-off between stages' },
  { name: 'Scatter plot', draws: 'Points on two measure axes', when: 'Whether two measures are related' },
  { name: 'Bubble chart', draws: 'A scatter whose point area is a third measure', when: 'Three measures at once' },
  { name: 'Heatmap', draws: 'A category × category grid shaded by a measure', when: 'Where the mass sits across two dimensions' },
  { name: 'Histogram', draws: 'A numeric column binned into a distribution', when: 'How a set of values is spread' },
  { name: 'World map', draws: 'A world map, countries in the result highlighted', when: 'Where in the world — availability or country-level counts' },
  { name: 'Table', draws: 'Every column, sortable, exact values', when: 'Detail and lookup' },
];

const RATIOS: Array<{ ratio: string; suits: string }> = [
  { ratio: '2 / 1', suits: 'KPI tiles' },
  { ratio: '16 / 9', suits: 'Trends and maps' },
  { ratio: '4 / 3', suits: 'Bar charts' },
  { ratio: '1 / 1', suits: 'Donuts' },
  { ratio: '3 / 2', suits: 'Hero cards' },
  { ratio: '3 / 4, 2 / 3, 4 / 5', suits: 'Leaderboards, tables and tall cards' },
];

export const Help = () => (
  <div className="help">
    <p className="help__lede">
      This is the authoring panel for appLockr Reports. You build three kinds of thing here — charts,
      dashboards and widgets — and the platform embeds what you build. Below is how each works, and a
      few rules the chart layer enforces on purpose.
    </p>

    <section className="help__section">
      <h3>The four things, and how they relate</h3>
      <ul className="help__cards">
        <li>
          <b>Query</b>
          <span>One of the 60 predefined backend queries. You don't author these — the data team does, and new ones appear here on their own.</span>
        </li>
        <li>
          <b>Chart</b>
          <span>A query paired with a form (bar, trend, table…), plus a name and description. The unit you place on dashboards and widgets.</span>
        </li>
        <li>
          <b>Dashboard</b>
          <span>Several charts on a 12-column grid. The platform points a content item at <code>/d/&lt;slug&gt;</code>.</span>
        </li>
        <li>
          <b>Widget</b>
          <span>One chart, no chrome, at a fixed aspect ratio for a platform grid card. Embedded at <code>/w/&lt;id&gt;</code>.</span>
        </li>
      </ul>
      <p className="help__note">
        There is <b>no SQL editor</b>, by design. The backend serves a fixed catalogue of queries;
        authoring is <i>choosing</i> a query and deciding how to draw it, not writing one. If a chart
        you need isn't possible, it's because the query isn't in the catalogue yet — that's a request
        to the data team, not something to build here.
      </p>
    </section>

    <section className="help__section">
      <h3>Making a chart</h3>
      <ol className="help__steps">
        <li><b>Data source.</b> App store data is live. IQ, Publisher and Advertiser are listed but greyed out until their APIs land.</li>
        <li><b>Query.</b> One of the 60. If it needs an app, the form says so — the platform supplies that at view time from the app picker.</li>
        <li><b>Preview values.</b> Stand-in parameters so you choose a form against real data. These preview only; they are <b>not saved</b>.</li>
        <li><b>Form.</b> One of the nine below, or accept the suggestion flagged <i>Suits this data</i> — that's the app reading the real response.</li>
        <li><b>Name and description.</b> The description is the <b>(i) tooltip</b> at the top-left of every chart — readers hover it to learn what the chart shows. Say what it measures and any caveat; it's the only place they find that out.</li>
        <li><b>Appearance (optional).</b> Style the card, title and — for a standard form — the chart itself with scoped CSS, or build the whole thing as <b>Custom HTML</b>. Both use the appLockr brand variables, so they stay on-brand. See below.</li>
      </ol>
      <div className="help__tablewrap">
        <table>
          <thead><tr><th>Form</th><th>Draws</th><th>Pick it when</th></tr></thead>
          <tbody>
            {FORMS.map((f) => (
              <tr key={f.name}><td><b>{f.name}</b></td><td>{f.draws}</td><td>{f.when}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="help__note">
        If a form looks wrong, the form is usually not the problem — a trend line across country names
        renders fine and means nothing. Preview every form against sample data, no login needed, at{' '}
        <a href="/preview">/preview</a>.
      </p>
    </section>

    <section className="help__section">
      <h3>Making it look nicer — brand-aligned styling</h3>
      <p>
        Two ways to go beyond the standard look, both in <b>Create</b> and <b>Edit</b>, and both using
        the appLockr brand variables so colours stay aligned with the platform (and follow light/dark):
      </p>
      <ul>
        <li><b>Appearance (CSS)</b> — restyle a standard chart's card, title and even the chart's SVG with your own CSS. It's <b>scoped to that one chart</b>, so it can never affect another. Brand-aligned one-click starters (Brand title, Lifted card…) get you going, and there's a list of the variables you can use (<code>--brand</code>, <code>--series-1…8</code>, <code>--ink</code>, <code>--surface</code>, …).</li>
        <li><b>Custom HTML</b> — build the whole chart as your own HTML (write it, or describe it and let AI draft it, then hand-edit). It renders sandboxed with <code>window.CHART_DATA</code> and the brand variables available. Brand-aligned starters (KPI number, Title band) and any saved template give you a head start.</li>
      </ul>
      <p className="help__note">
        Custom CSS is cosmetic and scoped, so the worst it can do is make one chart look odd — the exact
        numbers are always one click away in the Table twin.
      </p>
    </section>

    <section className="help__section">
      <h3>Saving and reusing templates</h3>
      <p>
        The forms above are built in, grouped in the picker by the question they answer. Beyond them you can build a chart with <b>Build with AI</b> (or
        by hand-editing its HTML) and then <b>save that as a template</b> — a reusable piece of markup you
        or anyone else can start the next chart from, against <i>any</i> query.
      </p>
      <ul>
        <li><b>Save one.</b> In a chart's <i>Build with AI</i> tab, once there's a preview, click <b>Save as template</b> and give it a name.</li>
        <li><b>Reuse one.</b> Either pick it from <i>Start from a saved template</i> in the Build-with-AI tab, or open the <b>Templates</b> tab and choose <b>New chart from this</b>. It loads the markup, which you can then tweak or re-prompt before saving the chart.</li>
        <li><b>Manage them.</b> The Templates tab lists every saved template and lets you delete ones you no longer need.</li>
      </ul>
      <p className="help__note">
        A template is a <b>snapshot</b>: applying it copies the markup into your chart, so editing the
        template later never silently changes charts already built from it (and never changes a live
        embed). Saving, editing and deleting templates needs an authorised (signed-in) account, the same
        as every other change here.
      </p>
    </section>

    <section className="help__section">
      <h3>Making a dashboard</h3>
      <ul>
        <li><b>Slug</b> is the field that matters. The platform points a content item at <code>/d/&lt;slug&gt;</code>, and it's the same on every environment — so cutting a report over is editing one field. Keep it stable once linked; changing it breaks the link.</li>
        <li><b>App filter</b> declares what the dashboard needs before it shows anything: <code>None</code> (same for everyone), <code>Single app</code> (<code>app_id</code>), or <code>Multiple apps</code> (<code>app_ids</code>). A dashboard that needs an app and isn't given one says so, rather than showing an empty grid.</li>
        <li><b>Layout</b> — add saved charts as tiles, then set each tile's width (1–12 columns) and height.</li>
      </ul>
      <p className="help__note">
        Filters appear above the grid automatically, and only when <b>every</b> tile can honour them —
        so the numbers on a dashboard always agree. The store filter is one; category follows the store
        (Android and iOS have different category lists, and a wrong one returns nothing).
      </p>
    </section>

    <section className="help__section">
      <h3>Making a widget</h3>
      <p>
        One chart, drawn chrome-less — the platform draws the card and heading. Pick the aspect ratio
        to match the platform card, and set the <i>same</i> ratio on the platform widget so the two
        agree.
      </p>
      <div className="help__tablewrap">
        <table>
          <thead><tr><th>Ratio</th><th>Suits</th></tr></thead>
          <tbody>
            {RATIOS.map((r) => (
              <tr key={r.ratio}><td><code>{r.ratio}</code></td><td>{r.suits}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="help__note">
        The card ratio is a hard constraint and the embed <b>must not scroll</b>. If content would
        overflow, it belongs on a dashboard, not in a widget.
      </p>
    </section>

    <section className="help__section">
      <h3>Rules the chart layer enforces</h3>
      <p>These are deliberate — each is a failure mode, not a preference.</p>
      <ul>
        <li><b>Every chart has a table twin.</b> The Table toggle is what makes a rounded figure or a value-in-a-tooltip acceptable — the exact numbers are one click away.</li>
        <li><b>Query order is kept.</b> Nothing re-sorts your results; a deliberate <code>ORDER BY</code> is never silently overridden.</li>
        <li><b>Series colours never cycle.</b> A ninth series folds into “Other” rather than reusing a colour, because two series sharing a colour is a chart that lies.</li>
        <li><b>Long results are truncated visibly.</b> Past 5,000 rows the card says “showing the first 5,000 of N”. Some catalogue queries return tens of thousands.</li>
        <li><b>Wildly different measures are dropped, and said so.</b> Measures more than 100× apart aren't plotted together; the card's note says which.</li>
      </ul>
    </section>

    <section className="help__section" id="registry">
      <h3>The ADO Chart Registry — keeping the platform team in step</h3>
      <p>
        Every chart, dashboard and widget you create here has a stable id (<code>cht_…</code>,
        <code> dsh_…</code>, <code>wgt_…</code>) and a description — the same string the (i) tooltip
        shows. The platform team wires those ids into content items, and they use the description
        to decide what a content item's label should say.
      </p>
      <p>
        So after every save, copy the new id + name + description into the{' '}
        <b>appLockr Reports → Chart Registry</b> page on the ADO wiki
        (<code>dev.azure.com/appLockr/Reports/_wiki</code>). Do it as part of the save, not
        "later" — the platform team pulls that page when they're preparing a release, and an item
        that isn't on the page won't get wired up. It's the same rule the seeded base items live
        by: their descriptions match Superset's own (i) copy verbatim, and they are listed at the
        top of the wiki page.
      </p>
      <p className="help__note">
        Description is <b>required</b> on the save form. The name says what the thing is called;
        the description says what it measures. Neither can be blank.
      </p>
    </section>

    <section className="help__section">
      <h3>The base reports (seeded on first start)</h3>
      <p>
        A fresh install has two dashboards and a handful of widgets pre-built, matching the
        Superset dashboards this app replaces — so the platform's existing content items keep
        working the moment they point at <code>/d/&lt;slug&gt;</code>.
      </p>
      <ul>
        <li>
          <code>/d/app-performance</code> — the App Performance layout: header KPIs, App
          Downloads, Revenue, Sentiment by Month, Ranking Volatility, App Availability (world
          map) and Similar Apps. Requires an <code>app_id</code> from the platform's app picker;
          in the studio it previews against Rovio's Angry Birds Friends
          (<code>com.rovio.angrybirdsfriends</code>) so every tile has something to draw.
        </li>
        <li>
          <code>/d/top-charts</code> — Top Free, Top Paid and Top Grossing side by side (Google
          Play), each an interactive ranked list with icons, half-precision stars and
          publisher / category on hover.
        </li>
      </ul>
      <p className="help__note">
        The base items are seeded <b>once</b> per id. Editing or deleting one is durable; the
        next start won't put it back. So take a copy before you rip one apart if you might want
        the original back.
      </p>
    </section>

    <section className="help__section">
      <h3>How the platform uses what you build</h3>
      <p>
        A finished dashboard or widget has a URL — <code>/d/&lt;slug&gt;</code> or <code>/w/&lt;id&gt;</code>.
        That's what gets plugged into a platform content item, which embeds it. Ordinary platform users
        never come here; they see your output inside the product. The platform passes the app selection
        and the viewer's identity in a signed token, so a report always shows the apps and partner data
        that viewer is allowed to see — you don't wire any of that up per chart.
      </p>
      <p className="help__note">
        Your own session here lasts the browser tab. A full refresh signs you out, by design — finish
        an edit before you reload.
      </p>
    </section>
  </div>
);
